import { useMemo } from "react";
import { usePaginate } from "~/components/pagination/usePaginate";
import { add, getDay } from "~/lib/date";
import {
  ExceptionStatus,
  ExceptionType,
  ListExceptionGroupsOrder,
  ListExceptionGroupsOrderField,
  OrderDirection,
  useListSpaceExceptionsQuery,
} from "~/operations";
import { useExceptionsFilterContext } from "./useExceptionsFilterContext";

export type UseSpaceExceptionsProps = {
  scopeMrn: string;
  sort?: {
    orderBy: ListExceptionGroupsOrder;
    handleSortClick: (id: string) => void;
  };
  paginate?: ReturnType<typeof usePaginate>;
};

export function useSpaceExceptions({
  scopeMrn,
  sort,
  paginate,
}: UseSpaceExceptionsProps) {
  const now = new Date();
  const nextWeek = add(now, { weeks: 1 });
  const validUntilRange = useMemo(
    () => ({ from: now.toISOString(), to: nextWeek.toISOString() }),
    [getDay(now), getDay(nextWeek)],
  );

  const filtering = useExceptionsFilterContext({ scopeMrn, paginate });
  const { filterVariables } = filtering;

  const defaultTypes = [
    ExceptionType.Security,
    ExceptionType.Advisory,
    ExceptionType.Cve,
    ExceptionType.Fex,
  ];

  const allExceptions = useListSpaceExceptionsQuery({
    variables: {
      ...paginate?.pageVars,
      input: {
        scopeMrn,
        types: filterVariables.types ?? defaultTypes,
        orderBy: sort?.orderBy || {
          direction: OrderDirection.Desc,
          field: ListExceptionGroupsOrderField.CreatedAt,
        },
        filter: {
          includeDeleted: true,
          includeExtension: true,
          ...filterVariables.filter,
        },
        includeChildScopes: true,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const pendingExceptions = useListSpaceExceptionsQuery({
    variables: {
      input: {
        scopeMrn,
        types: defaultTypes,
        orderBy: {
          direction: OrderDirection.Desc,
          field: ListExceptionGroupsOrderField.CreatedAt,
        },
        filter: {
          statuses: [ExceptionStatus.New],
        },
        includeChildScopes: true,
      },
      first: 4,
    },
    fetchPolicy: "cache-and-network",
  });

  const expiringExceptions = useListSpaceExceptionsQuery({
    variables: {
      input: {
        scopeMrn,
        types: defaultTypes,
        orderBy: {
          direction: OrderDirection.Asc,
          field: ListExceptionGroupsOrderField.ValidUntil,
        },
        filter: {
          statuses: [
            ExceptionStatus.Approved,
            ExceptionStatus.New,
            ExceptionStatus.NeedsExtension,
          ],
          validUntilRange,
        },
        includeChildScopes: true,
      },
      first: 4,
    },
    fetchPolicy: "cache-and-network",
  });

  return {
    allExceptions,
    pendingExceptions,
    expiringExceptions,
    filtering,
  };
}
